import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { createReservation, getUnits, checkConflict, fmt, fmtDate } from "@/lib/api";

const initialForm = () => ({
  guest_name: "",
  guest_email: "",
  unit_id: "",
  check_in: new Date().toISOString().slice(0, 10),
  check_out: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
  accommodation_total: "",
  notes: "",
  channel: "",
  commission_amount: "",
  extra_guests: "",
  extra_guest_names: [],
  prepayment_amount: "",
  prepayment_method: "cash",
  dietary_notes: "",
  severe_allergy: false,
});

export default function AddReservationDialog({ open, onClose, onSaved }) {
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [conflict, setConflict] = useState(null); // { conflict, reservations }

  useEffect(() => {
    if (open) {
      getUnits().then(setUnits).catch(() => {});
      setForm(initialForm());
      setConflict(null);
    }
  }, [open]);

  // Live conflict check whenever unit + dates are all set
  useEffect(() => {
    if (!form.unit_id || !form.check_in || !form.check_out || form.check_in >= form.check_out) {
      setConflict(null);
      return;
    }
    const t = setTimeout(() => {
      checkConflict({
        unit_id: form.unit_id,
        check_in: form.check_in,
        check_out: form.check_out,
      }).then(setConflict).catch(() => setConflict(null));
    }, 250);
    return () => clearTimeout(t);
  }, [form.unit_id, form.check_in, form.check_out]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const totalNum = parseFloat(form.accommodation_total) || 0;
  const prepayNum = parseFloat(form.prepayment_amount) || 0;
  const remaining = useMemo(() => Math.max(0, totalNum - prepayNum), [totalNum, prepayNum]);

  const handleSave = async () => {
    if (!form.guest_name || !form.unit_id) { toast.error("Guest and unit required"); return; }
    if (prepayNum > totalNum) { toast.error("Prepayment can't exceed total"); return; }
    try {
      await createReservation({
        guest_name: form.guest_name,
        guest_email: form.guest_email,
        unit_id: form.unit_id,
        check_in: form.check_in,
        check_out: form.check_out,
        accommodation_total: totalNum,
        notes: form.notes,
        channel: form.channel.trim(),
        commission_amount: parseFloat(form.commission_amount) || 0,
        extra_guests: Math.max(0, parseInt(form.extra_guests) || 0),
        extra_guest_names: (form.extra_guest_names || [])
          .slice(0, Math.max(0, parseInt(form.extra_guests) || 0))
          .map(n => (n || "").trim())
          .filter(Boolean),
        prepayment_amount: prepayNum,
        prepayment_method: prepayNum > 0 ? form.prepayment_method : null,
        dietary_notes: (form.dietary_notes || "").trim(),
        severe_allergy: !!form.severe_allergy,
      });
      toast.success(prepayNum > 0 ? `Reservation created · ${fmt(prepayNum)} prepaid` : "Reservation created");
      onSaved?.();
      onClose();
    } catch {
      toast.error("Failed to create reservation");
    }
  };

  const grouped = units.reduce((acc, u) => {
    (acc[u.group] = acc[u.group] || []).push(u);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-reservation-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New Reservation</DialogTitle>
          <DialogDescription className="sr-only">Create a new guest reservation with optional prepayment.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Guest Name</Label>
            <Input
              value={form.guest_name}
              onChange={(e) => upd("guest_name", e.target.value)}
              className="mt-1 h-11"
              placeholder="e.g. Marta Silva"
              data-testid="reservation-guest-input"
              autoFocus
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Guest Email <span className="text-slate-300 normal-case tracking-normal">(optional)</span></Label>
            <Input
              type="email"
              value={form.guest_email}
              onChange={(e) => upd("guest_email", e.target.value)}
              className="mt-1 h-11"
              placeholder="guest@example.com"
              data-testid="reservation-email-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Booking Channel</Label>
              <Input
                value={form.channel}
                onChange={(e) => upd("channel", e.target.value)}
                className="mt-1 h-11"
                placeholder="Direct, Booking.com, Email..."
                list="channel-suggestions"
                data-testid="reservation-channel-input"
              />
              <datalist id="channel-suggestions">
                <option value="Direct" />
                <option value="Booking.com" />
                <option value="Airbnb" />
                <option value="Expedia" />
                <option value="Hostelworld" />
                <option value="Email" />
                <option value="Phone" />
                <option value="Walk-in" />
              </datalist>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Commission (€) <span className="text-slate-300 normal-case tracking-normal">(optional)</span></Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.commission_amount}
                onChange={(e) => upd("commission_amount", e.target.value)}
                className="mt-1 h-11"
                placeholder="0.00"
                data-testid="reservation-commission-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Unit / Bed</Label>
            <Select value={form.unit_id} onValueChange={(v) => upd("unit_id", v)}>
              <SelectTrigger className="mt-1 h-11" data-testid="reservation-unit-select">
                <SelectValue placeholder="Select unit..." />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(grouped).map(([group, us]) => (
                  <div key={group}>
                    <div className="px-2 py-1.5 text-xs uppercase tracking-wider text-slate-400">{group}</div>
                    {us.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.display_name}</SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Check-in</Label>
              <Input type="date" value={form.check_in} onChange={(e) => upd("check_in", e.target.value)} className="mt-1 h-11" data-testid="reservation-checkin-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Check-out</Label>
              <Input type="date" value={form.check_out} onChange={(e) => upd("check_out", e.target.value)} className="mt-1 h-11" data-testid="reservation-checkout-input" />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Additional guests <span className="text-slate-300 normal-case tracking-normal">(on top of main guest)</span></Label>
            <Input
              type="number"
              min="0"
              step="1"
              value={form.extra_guests}
              onChange={(e) => {
                const n = Math.max(0, parseInt(e.target.value) || 0);
                setForm(f => {
                  const prev = f.extra_guest_names || [];
                  const next = Array.from({ length: n }, (_, i) => prev[i] || "");
                  return { ...f, extra_guests: e.target.value, extra_guest_names: next };
                });
              }}
              className="mt-1 h-11"
              placeholder="0"
              data-testid="reservation-extra-guests-input"
            />
            <div className="text-[11px] text-slate-400 mt-1" data-testid="reservation-total-guests-hint">
              Total in the room: {1 + Math.max(0, parseInt(form.extra_guests) || 0)} guest{(1 + Math.max(0, parseInt(form.extra_guests) || 0)) === 1 ? "" : "s"}
            </div>
            {Math.max(0, parseInt(form.extra_guests) || 0) > 0 && (
              <div className="mt-3 space-y-2" data-testid="reservation-extra-guest-names">
                {Array.from({ length: Math.max(0, parseInt(form.extra_guests) || 0) }).map((_, i) => (
                  <Input
                    key={i}
                    value={form.extra_guest_names?.[i] || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm(f => {
                        const arr = [...(f.extra_guest_names || [])];
                        arr[i] = v;
                        return { ...f, extra_guest_names: arr };
                      });
                    }}
                    placeholder={`Additional guest ${i + 1} name`}
                    className="h-10"
                    data-testid={`reservation-extra-guest-name-${i}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Conflict warning */}
          {conflict?.conflict && (
            <div className="border border-red-300 bg-red-50 rounded-md p-3 flex gap-2" data-testid="conflict-warning">
              <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-red-700">Double-booking warning</div>
                <div className="text-red-600 text-xs mt-0.5">This unit already has {conflict.count} reservation{conflict.count === 1 ? "" : "s"} overlapping these dates:</div>
                <ul className="mt-1.5 space-y-0.5">
                  {conflict.reservations.slice(0, 3).map(r => (
                    <li key={r.id} className="text-xs text-red-700">
                      · <span className="font-medium">{r.guest_name}</span> ({fmtDate(r.check_in)} → {fmtDate(r.check_out)})
                    </li>
                  ))}
                </ul>
                <div className="text-[11px] text-red-500 mt-1">You can still save, but the same bed will be booked twice.</div>
              </div>
            </div>
          )}
          {conflict && !conflict.conflict && form.unit_id && (
            <div className="text-xs text-emerald-700 flex items-center gap-1.5" data-testid="availability-ok">
              <span className="inline-block h-2 w-2 rounded-full bg-emerald-500"></span> Available for these dates
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Accommodation Total (€)</Label>
            <Input
              type="number"
              step="0.01"
              value={form.accommodation_total}
              onChange={(e) => upd("accommodation_total", e.target.value)}
              className="mt-1 h-11"
              placeholder="0.00"
              data-testid="reservation-total-input"
            />
          </div>

          {/* Prepayment block */}
          <div className="border border-slate-200 rounded-md p-4 bg-slate-50/60 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading font-semibold text-slate-900 text-sm">Prepayment</div>
                <div className="text-xs text-slate-500">Has the guest already paid a deposit? Leave as 0 if not.</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Amount Paid (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.prepayment_amount}
                  onChange={(e) => upd("prepayment_amount", e.target.value)}
                  className="mt-1 h-11"
                  placeholder="0.00"
                  data-testid="reservation-prepayment-amount"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Method</Label>
                <Select
                  value={form.prepayment_method}
                  onValueChange={(v) => upd("prepayment_method", v)}
                  disabled={prepayNum <= 0}
                >
                  <SelectTrigger className="mt-1 h-11" data-testid="reservation-prepayment-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash / Money</SelectItem>
                    <SelectItem value="card">Credit Card</SelectItem>
                    <SelectItem value="transfer">Bank Transfer</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Live remaining summary */}
            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-200">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div>
                <div className="font-heading font-bold text-slate-900 text-sm mt-0.5">{fmt(totalNum)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Prepaid</div>
                <div className="font-heading font-bold text-emerald-600 text-sm mt-0.5">{fmt(prepayNum)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Due at check-in</div>
                <div className={`font-heading font-bold text-sm mt-0.5 ${remaining > 0 ? "text-red-600" : "text-slate-900"}`} data-testid="reservation-remaining-preview">
                  {fmt(remaining)}
                </div>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => upd("notes", e.target.value)}
              className="mt-1"
              rows={2}
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Dietary requirements <span className="text-slate-300 normal-case tracking-normal">(kitchen)</span></Label>
              <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.severe_allergy}
                  onChange={(e) => upd("severe_allergy", e.target.checked)}
                  className="accent-red-500"
                  data-testid="reservation-severe-allergy"
                />
                <AlertTriangle size={11} className="text-red-500" /> Severe allergy
              </label>
            </div>
            <Input
              value={form.dietary_notes}
              onChange={(e) => upd("dietary_notes", e.target.value)}
              placeholder="e.g. Vegan, gluten free, nut allergy"
              className="mt-1 h-10"
              data-testid="reservation-dietary-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="reservation-dialog-save">
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
